import React, { useEffect, useState } from 'react';
import { Card, Pagination } from 'react-bootstrap';
import { toast } from 'react-toastify';
import LoadingSpinner from '../components/common/LoadingSpinner/LoadingSpinner';
import ErrorAlert from '../components/common/ErrorAlert/ErrorAlert';
import UserFilters from '../components/Users/UserFilters/UserFilters';
import UserList from '../components/Users/UserList/UserList';
import UserProfileModal from '../components/Users/UserProfileModal/UserProfileModal';
import { userService } from '../services/userService';

const PAGE_SIZE = 50;

const UsersPage = () => {
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [filters, setFilters] = useState({});
  const [page, setPage] = useState(1);
  const [total, setTotal] = useState(0);
  const [totalPages, setTotalPages] = useState(0);
  const [selectedUser, setSelectedUser] = useState(null);
  const [showProfileModal, setShowProfileModal] = useState(false);

  // Один путь для всех случаев: фильтры и поиск уходят теми же параметрами,
  // что и страница, поэтому перелистывание работает и при активном поиске.
  const loadUsers = async (appliedFilters = filters, pageNum = 1) => {
    setLoading(true);
    setError(null);
    try {
      const data = await userService.getAllUsersForAdmin({
        page: pageNum,
        limit: PAGE_SIZE,
        search: appliedFilters.search,
        status: appliedFilters.status,
        verified: appliedFilters.verified,
      });
      setUsers(data?.users || []);
      setTotal(data?.total ?? 0);
      setTotalPages(data?.totalPages ?? 1);
      setPage(data?.page ?? pageNum);
    } catch (e) {
      setError(e);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadUsers();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);
  useEffect(() => {
  }, [users]);

  const handleFiltersChange = async (newFilters) => {
    setFilters(newFilters);
    await loadUsers(newFilters, 1);
  };

  const handlePageChange = (pageNum) => {
    if (pageNum < 1 || pageNum > totalPages) return;
    loadUsers(filters, pageNum);
  };

  const handleOpenProfile = (user) => {
    setSelectedUser(user);
    setShowProfileModal(true);
  };

  const handleBlockToggle = async (user, block) => {
    try {
      if (block) {
        await userService.blockUser({ userId: user._id });
        toast.success('Пользователь заблокирован');
      } else {
        await userService.unblockUser({ userId: user._id });
        toast.success('Пользователь разблокирован');
      }
      await loadUsers(filters, page);
    } catch (e) {
      toast.error(e?.response?.data?.message || 'Ошибка при изменении статуса');
    }
  };

  const handleDelete = async (user) => {
    if (!window.confirm('Удалить аккаунт пользователя?')) return;
    try {
      await userService.deleteUser(user._id);
      toast.success('Пользователь удалён');
      // Остаёмся на той же странице, а если удалили последнюю запись на ней —
      // откатываемся на предыдущую, чтобы не показывать пустой список.
      const nextPage = users.length === 1 && page > 1 ? page - 1 : page;
      await loadUsers(filters, nextPage);
    } catch (e) {
      toast.error(e?.response?.data?.message || 'Ошибка удаления пользователя');
    }
  };

  const buildPages = (currentPage, totalPages) => {
    const pages = [];
    if (totalPages <= 7) {
      for (let i = 1; i <= totalPages; i += 1) pages.push(i);
      return pages;
    }
    if (currentPage <= 3) {
      return [1, 2, 3, '...', totalPages];
    }
    if (currentPage >= totalPages - 2) {
      return [1, '...', totalPages - 2, totalPages - 1, totalPages];
    }
    return [1, '...', currentPage - 1, currentPage, currentPage + 1, '...', totalPages];
  };

  return (
    <>
      <div className="d-flex justify-content-between align-items-end mb-3">
        <h1 className="mb-0">Пользователи</h1>
        <div className="text-muted small">Всего: {total}</div>
      </div>
      <Card className="mb-3">
        <Card.Body>
          <UserFilters onChange={handleFiltersChange} />
        </Card.Body>
      </Card>
      {loading && !users.length ? (
        <LoadingSpinner />
      ) : (
        <>
          <ErrorAlert error={error} />
          <UserList users={users} onView={handleOpenProfile} />
          {totalPages > 1 && (
            <div className="d-flex justify-content-center mt-3">
              <Pagination>
                <Pagination.Prev
                  disabled={page <= 1}
                  onClick={() => handlePageChange(page - 1)}
                />
                {buildPages(page, totalPages).map((p, idx) =>
                  p === '...' ? (
                    <Pagination.Ellipsis key={`e-${idx}`} disabled />
                  ) : (
                    <Pagination.Item
                      key={p}
                      active={p === page}
                      onClick={() => handlePageChange(p)}
                    >
                      {p}
                    </Pagination.Item>
                  ),
                )}
                <Pagination.Next
                  disabled={page >= totalPages}
                  onClick={() => handlePageChange(page + 1)}
                />
              </Pagination>
            </div>
          )}
        </>
      )}
      <UserProfileModal
        show={showProfileModal}
        onHide={() => setShowProfileModal(false)}
        user={selectedUser}
        onBlockToggle={handleBlockToggle}
        onDelete={handleDelete}
      />
    </>
  );
};

export default UsersPage;

